import "./ProfilePage.css";

import { Link } from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import DraggableModal from "../components/layout/DraggableModal/DraggableModal";
import LanguageSwitcher from "../components/utils/LanguageSwitcher/LanguageSwitcher";
import { updateProfileImage } from "../clients/profile.client";
import { updateUserImage } from "../reducers/auth.reducer";

const ProfilePage = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { user, tokenValue } = useSelector((state) => state.auth);
  const [imageUrl, setImageUrl] = useState("");
  const [imageLoadFailed, setImageLoadFailed] = useState(false);
  const [isImageDialogOpen, setIsImageDialogOpen] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateError, setUpdateError] = useState("");
  const [updateMessage, setUpdateMessage] = useState("");
  const imageInputRef = useRef(null);

  const characterCount = Array.isArray(user?.characterIds)
    ? user.characterIds.length
    : 0;
  const isAdmin = Array.isArray(user?.roles) && user.roles.includes("ADMIN");
  const accountType = isAdmin ? t("profile.roleAdmin") : t("profile.rolePlayer");
  const profileInitial = useMemo(
    () => String(user?.email ?? "?").trim().charAt(0).toUpperCase() || "?",
    [user?.email]
  );
  const hasProfileImage = Boolean(user?.image) && !imageLoadFailed;

  useEffect(() => {
    setImageLoadFailed(false);
  }, [user?.image]);

  const openImageDialog = () => {
    setImageUrl(user?.image ?? "");
    setUpdateError("");
    setUpdateMessage("");
    setIsImageDialogOpen(true);
  };

  const closeImageDialog = () => {
    if (!isUpdating) setIsImageDialogOpen(false);
  };

  const handleImageUpdate = async (event) => {
    event.preventDefault();
    const normalizedUrl = imageUrl.trim();

    setIsUpdating(true);
    setUpdateError("");
    const response = await updateProfileImage(normalizedUrl, tokenValue);

    if (response.isError) {
      setUpdateError(t("profile.updateError"));
      setIsUpdating(false);
      return;
    }

    dispatch(updateUserImage(normalizedUrl));
    setImageLoadFailed(false);
    setUpdateMessage(t("profile.updateSuccess"));
    setIsUpdating(false);
    setIsImageDialogOpen(false);
  };

  return (
    <main className="profile-page">
      <header className="profile-page__header">
        <p>{t("profile.eyebrow")}</p>
        <h1>{t("profile.title")}</h1>
        <span>{t("profile.subtitle")}</span>
      </header>

      <section className="profile-card" aria-labelledby="profile-email">
        <div className="profile-card__identity">
          <div className="profile-avatar">
            {hasProfileImage ? (
              <img
                alt={t("profile.imageAlt", { email: user.email })}
                src={user.image}
                onError={() => setImageLoadFailed(true)}
              />
            ) : (
              <span aria-hidden="true">{profileInitial}</span>
            )}
          </div>
          <button className="profile-secondary-button" type="button" onClick={openImageDialog}>
            {t("profile.changeImage")}
          </button>
          {updateMessage && (
            <p className="profile-update-message" role="status">
              {updateMessage}
            </p>
          )}
        </div>

        <div className="profile-card__content">
          <section className="profile-section">
            <h2>{t("profile.accountDetails")}</h2>
            <dl className="profile-details">
              <div>
                <dt>{t("profile.email")}</dt>
                <dd id="profile-email">{user.email}</dd>
              </div>
              <div>
                <dt>{t("profile.accountType")}</dt>
                <dd><span className="profile-role-badge">{accountType}</span></dd>
              </div>
              <div>
                <dt>{t("language.label")}</dt>
                <dd><LanguageSwitcher showLabel={false} variant="profile" /></dd>
              </div>
            </dl>
          </section>

          <section className="profile-section profile-characters">
            <div>
              <h2>{t("profile.characters")}</h2>
              <p>{t("profile.charactersDescription")}</p>
            </div>
            <div className="profile-character-summary">
              <strong>{characterCount}</strong>
              <span>{t("profile.charactersCountLabel")}</span>
            </div>
            <Link className="profile-primary-link" to="/characters">
              {t("profile.openCharacters")}
            </Link>
          </section>
        </div>
      </section>

      <DraggableModal
        className="profile-image-dialog"
        closeDisabled={isUpdating}
        headerText={t("profile.changeImage")}
        initialFocusRef={imageInputRef}
        isVisible={isImageDialogOpen}
        onClose={closeImageDialog}
      >
        <form className="profile-image-form" onSubmit={handleImageUpdate}>
          <label htmlFor="profile-image-url">{t("profile.imageUrl")}</label>
          <input
            aria-describedby={updateError ? "profile-image-hint profile-image-error" : "profile-image-hint"}
            aria-invalid={Boolean(updateError)}
            autoComplete="url"
            id="profile-image-url"
            name="imageUrl"
            placeholder={t("profile.imagePlaceholder")}
            ref={imageInputRef}
            required
            type="url"
            value={imageUrl}
            onChange={(event) => setImageUrl(event.target.value)}
          />
          <small id="profile-image-hint">{t("profile.imageHint")}</small>
          {updateError && (
            <p className="profile-image-error" id="profile-image-error" role="alert">
              {updateError}
            </p>
          )}
          <div className="profile-image-form__actions">
            <button disabled={isUpdating} type="button" onClick={closeImageDialog}>
              {t("common.cancel")}
            </button>
            <button className="profile-image-submit" disabled={isUpdating} type="submit">
              {isUpdating ? t("profile.updating") : t("profile.saveImage")}
            </button>
          </div>
        </form>
      </DraggableModal>
    </main>
  );
};

export default ProfilePage;
